import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Heart, ShoppingBag, Search, Menu, X, User, LogOut } from "lucide-react";
import { useStore } from "@/context/StoreContext";
import { useAuth } from "@/context/AuthContext";

const links = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/shop", label: "Shop" },
  { to: "/combos", label: "Combos" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const { totals, wishlist } = useStore();
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <header
      data-testid="site-navbar"
      className="fixed top-0 inset-x-0 z-50 bg-[#FDF0F4] border-b border-[#F7C7DC] transition-all duration-300 shadow-sm"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-3 flex items-center justify-between gap-4">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group shrink-0">
          <img src="/logo.png" alt="ALAIRA HOUSE Logo" className="w-12 h-12 md:w-16 md:h-16 rounded-full object-cover group-hover:scale-105 transition duration-300 shadow-md border border-[#F7C7DC]" />
          <span className="font-display font-bold tracking-wide text-lg md:text-2xl text-[#1C1924] select-none whitespace-nowrap">
            ALAIRA <span className="text-[#C4A55A] font-semibold italic font-serif-italic">HOUSE</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `text-[13.5px] font-medium tracking-wide transition-colors ${isActive ? "text-[#E5497C] font-semibold" : "text-[#4A4652] hover:text-[#E5497C]"}`
              }
              end
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-1 md:gap-2">
          <button
            data-testid="nav-search-btn"
            onClick={() => navigate("/shop")}
            className="hidden md:grid w-10 h-10 place-items-center rounded-full hover:bg-[#FCE8EE] transition text-[#1C1924]"
            aria-label="Search"
          >
            <Search size={18} />
          </button>

          {user ? (
            <div className="relative group flex items-center">
              <button
                data-testid="nav-profile-btn"
                onClick={() => navigate("/profile")}
                className="w-10 h-10 rounded-full bg-[#FCE8EE] hover:bg-[#F7D2E0] transition text-[#9E2A50] font-display font-bold text-[13px] grid place-items-center cursor-pointer shadow-sm border border-[#F7C7DC]"
              >
                {user.name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2)}
              </button>

              <div className="absolute right-0 top-10 pt-2 w-48 opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-opacity duration-300">
                <div className="bg-white border border-[#F7C7DC] rounded-2xl shadow-xl p-2 flex flex-col">
                  <div className="px-3 py-2 border-b border-[#FCE8EE] mb-1">
                    <p className="text-[13px] font-semibold text-[#1C1924] truncate">{user.name}</p>
                    <p className="text-[11px] text-[#8b8790] truncate">{user.email}</p>
                  </div>
                  <Link
                    to="/profile"
                    className="flex items-center gap-2 w-full text-left px-3 py-2 text-[13px] text-[#1C1924] hover:bg-[#FCE8EE] rounded-xl transition font-medium mb-1"
                  >
                    <User size={14} /> My Profile
                  </Link>
                  {(user.role === "admin" || ["yedageshantanu@gmail.com", "yedageshantanu70@gmail.com", "pawartanu417@gmail.com"].includes(user.email?.toLowerCase())) && (
                    <Link
                      to="/admin"
                      className="flex items-center gap-2 w-full text-left px-3 py-2 text-[13px] text-gold hover:bg-[#FCE8EE] rounded-xl transition font-semibold mb-1"
                    >
                      <User size={14} className="text-gold" /> Admin Panel
                    </Link>
                  )}
                  <button
                    data-testid="logout-button"
                    onClick={logout}
                    className="flex items-center gap-2 w-full text-left px-3 py-2 text-[13px] text-red-500 hover:bg-red-50 rounded-xl transition font-medium"
                  >
                    <LogOut size={14} /> Sign Out
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <Link
              to="/login"
              data-testid="nav-login-btn"
              className="grid w-10 h-10 place-items-center rounded-full hover:bg-[#FCE8EE] transition text-[#1C1924]"
              aria-label="Sign In"
            >
              <User size={18} />
            </Link>
          )}

          <Link
            to="/wishlist"
            data-testid="nav-wishlist-icon"
            className="relative grid w-10 h-10 place-items-center rounded-full hover:bg-[#FCE8EE] transition text-[#1C1924]"
            aria-label="Wishlist"
          >
            <Heart size={18} />
            {wishlist.length > 0 && (
              <span data-testid="nav-wishlist-count" className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#E5497C] text-white text-[10px] font-bold grid place-items-center">{wishlist.length}</span>
            )}
          </Link>
          <Link
            to="/cart"
            data-testid="nav-cart-icon"
            className="relative grid w-10 h-10 place-items-center rounded-full hover:bg-[#FCE8EE] transition text-[#1C1924]"
            aria-label="Cart"
          >
            <ShoppingBag size={18} />
            {totals.itemCount > 0 && (
              <span data-testid="nav-cart-count" className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#1C1924] text-white text-[10px] font-bold grid place-items-center">{totals.itemCount}</span>
            )}
          </Link>
          <button
            data-testid="nav-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="md:hidden grid w-10 h-10 place-items-center rounded-full hover:bg-[#FCE8EE] transition text-[#1C1924]"
            aria-label="Menu"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="md:hidden border-t border-[#F7C7DC] bg-[#FDF0F4] px-6 py-4">
          <div className="flex flex-col gap-1">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                className="py-3 px-4 rounded-xl text-[#1C1924] hover:bg-[#FCE8EE] text-[15px] font-medium transition"
                end
              >
                {l.label}
              </NavLink>
            ))}
            {user ? (
              <>
                <div className="py-2 px-3 border-t border-[#F7C7DC] mt-2 text-[14px]">
                  <p className="font-semibold text-[#1C1924]">{user.name}</p>
                  <p className="text-[12px] text-[#8b8790]">{user.email}</p>
                </div>
                {(user.role === "admin" || ["yedageshantanu@gmail.com", "yedageshantanu70@gmail.com", "pawartanu417@gmail.com"].includes(user.email?.toLowerCase())) && (
                  <NavLink
                    to="/admin"
                    onClick={() => setOpen(false)}
                    className="py-3 px-3 rounded-xl text-gold hover:bg-gold/5 text-[15px] font-semibold flex items-center gap-2"
                  >
                    Admin Panel
                  </NavLink>
                )}
                <button
                  data-testid="logout-button"
                  onClick={() => { setOpen(false); logout(); }}
                  className="mt-1 py-3 px-3 rounded-xl text-red-500 hover:bg-red-50 text-[15px] font-medium text-left flex items-center gap-2"
                >
                  <LogOut size={16} /> Sign Out
                </button>
              </>
            ) : (
              <NavLink
                to="/login"
                onClick={() => setOpen(false)}
                data-testid="mobile-nav-link-login"
                className="py-3 px-3 rounded-xl text-[#1C1924] hover:bg-white text-[15px] font-medium border-t border-[#F7C7DC] mt-2"
              >
                Sign In
              </NavLink>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
